import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PaymentSelect from "../Payment/PaymentSelect";
import Stepper from "../Stepper/Stepper";
import AccountInfo from "./AccountInfo";
import { AirportProvider, AirportRideContext } from "./AirportRideContext";
import RideDetailsStep from "./RideDetailsStep";
import VehicleDetailsStep from "./VehicleDetailsStep";

/**
 * The main entry into the Airport trip booking process
 *
 * It first shows the ride details form, then shows the vechile details form, then, if the user isn't logged in, it shows a form to complete the user information and register them.
 * After all the forms are finished, is shows the user a form to select the payment method
 *
 * @returns {JSX.Element}
 */

const AirportRideWrapper = () => (
  <AirportProvider>
    <AirportRide />
  </AirportProvider>
);

const AirportRide = () => {
  const { t } = useTranslation("global");
  const isLoggedIn = useSelector((state) => state.authData.user) !== null;
  const { activeTab, firstStepValues, secondStepValues, setActiveTab } =
    useContext(AirportRideContext);
  const { user } = useSelector((state) => state.authData);
  const isRegistered = !!user;
  const steps = useMemo(() => {
    const baseSteps = [
      { id: 1, text: t("hero.stepper_steps.ride_detail_text") },
      { id: 2, text: t("hero.stepper_steps.vehicle_detail_text") },
    ];
    if (!isLoggedIn) {
      baseSteps.push({
        id: 4,
        text: t("hero.stepper_steps.account_info_text"),
      });
    }
    return baseSteps;
  }, [isLoggedIn, t]);
  return (
    <div>
      {(activeTab <= 2 || !isRegistered) && (
        <Stepper
          steps={steps}
          subTab={activeTab}
          className={
            "flex items-center w-full text-sm font-medium text-center py-4 border-b text-gray-500 sm:text-base justify-between mb-4"
          }
        />
      )}
      {activeTab === 1 && <RideDetailsStep />}
      {activeTab === 2 && <VehicleDetailsStep />}
      {activeTab === 3 &&
        (isRegistered ? (
          <PaymentSelect
            formValues={{ ...firstStepValues, ...secondStepValues }}
            setActiveTab={setActiveTab}
            serviceType="Airport Trip"
          />
        ) : (
          <AccountInfo />
        ))}
    </div>
  );
};

export default AirportRideWrapper;
